import { erpApi } from '@/shared/axios/erp-api';
import { urlBackend } from '@/shared/constants';

/**
 * Cobro de la ONT entregada en un traslado (cuando el cliente no conserva la
 * suya). Lo guarda Django en `agendamiento.observaciones.ont_cobro`, en una
 * clave que solo escribe ese endpoint: así la tarjeta de "Otros conceptos", que
 * manda las observaciones completas desde su copia, no lo borra al guardar.
 *
 * El IVA y el total NO se mandan: los calcula el servidor con el porcentaje del
 * catálogo. Acá se calculan solo para mostrarlos mientras el operador escribe.
 */

const { post } = erpApi();

/** Lo que el formulario mantiene mientras se edita. */
export type CobroOntForm = {
  tipo_pago: string;
  meses_pago?: number | null;
  valor_sin_iva: number;
};

export type CobroOntGuardado = CobroOntForm & {
  concepto: string;
  iva: number;
  valor_total: number;
  codigo: string;
  series: string[];
  origen: string;
  usuario?: string;
  fecha?: string;
};

/** Guarda (o reemplaza) el cobro de la ONT entregada. */
export async function guardarCobroOnt(
  idOrdenTrabajo: string,
  body: {
    serie: string;
    codigo: string;
    descripcion: string;
    tipo_pago: string;
    meses_pago?: number | null;
    valor_sin_iva: number;
  },
): Promise<CobroOntGuardado> {
  const res = await post<CobroOntGuardado>(
    `${urlBackend}api/v1/orden-trabajo/${idOrdenTrabajo}/ont-cobro/`,
    body as any,
    true,
  );
  if (res.status === 'error') {
    throw new Error(res.message || 'No se pudo guardar el cobro de la ONT.');
  }
  return res.data;
}

/** Quita el cobro (se usa al pasar a la ONT del propio cliente). */
export async function quitarCobroOnt(idOrdenTrabajo: string): Promise<void> {
  const res = await post<unknown>(
    `${urlBackend}api/v1/orden-trabajo/${idOrdenTrabajo}/ont-cobro/`,
    { quitar: true } as any,
    true,
  );
  if (res.status === 'error') {
    throw new Error(res.message || 'No se pudo quitar el cobro de la ONT.');
  }
}

/** Monto redondeado a 2 decimales, con el mismo criterio que el backend. */
export function r2(n: number): number {
  return Math.round((Number(n) || 0) * 100 + 1e-9) / 100;
}

/** IVA y total para mostrar mientras se escribe (el servidor los recalcula). */
export function montosCobroOnt(valorSinIva: number, ivaPorcentaje: number) {
  const sinIva = r2(valorSinIva);
  const iva = r2(sinIva * ((Number(ivaPorcentaje) || 0) / 100));
  return { valor_sin_iva: sinIva, iva, valor_total: r2(sinIva + iva) };
}

/** El cobro ya guardado en la OT, para precargar el formulario. */
export function cobroGuardadoDe(ordenTrabajo: any): CobroOntGuardado | null {
  const obs = ordenTrabajo?.agendamiento_data?.observaciones;
  const cobro = obs && typeof obs === 'object' ? (obs as any).ont_cobro : null;
  return cobro && typeof cobro === 'object' ? (cobro as CobroOntGuardado) : null;
}
